// Founder-ops data layer — ported from `frontend/src/lib/data/ops.ts`
// (task-23 brief: "COPY frontend/src/lib/data/ops.ts into the portal — don't
// rewrite; its approve() 409 no_host_agreement unwrapping via
// error.context.json() is subtle and battle-tested"). The only changes from
// the original: the response interfaces now live in
// `@namibiago/shared/types` (moved there in an earlier task so mobile and
// portal share one definition — see packages/shared/src/types/ops.ts), and
// every call takes the admin app's own Supabase client instead of importing
// a module-level singleton, so this module has no import-time dependency on
// which target (`business`/`admin`) is running.
//
// All calls hit founder-gated edge functions with the signed-in admin's
// JWT; a non-admin gets 403, which the UI treats as "not available". There
// is deliberately no client-side privilege check here — the server
// (`profiles.is_admin` ∪ `FOUNDER_USER_IDS`) is the only gate.

import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  ApprovedBusiness,
  BusinessDetail,
  PendingBusiness,
  ReviewPayload,
} from '@namibiago/shared/types';
import type { Reason } from '../api/reason';

export type {
  PendingKycDoc,
  ReviewStatus,
  PendingBusiness,
  DetailKycDoc,
  BusinessDetail,
  ApprovedBusiness,
  ReviewAction,
  ReviewPayload,
} from '@namibiago/shared/types';

export const opsDB = {
  async listPending(supabase: SupabaseClient): Promise<{ data: PendingBusiness[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.functions.invoke('ops-pending', { method: 'GET' });
      if (error) return { data: null, error: new Error(error.message ?? 'Forbidden') };
      return { data: (data?.pending ?? []) as PendingBusiness[], error: null };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e : new Error(String(e)) };
    }
  },

  async listApproved(supabase: SupabaseClient): Promise<{ data: ApprovedBusiness[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.functions.invoke('ops-pending', {
        body: { scope: 'approved' },
      });
      if (error) return { data: null, error: new Error(error.message ?? 'Forbidden') };
      return { data: (data?.approved ?? []) as ApprovedBusiness[], error: null };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e : new Error(String(e)) };
    }
  },

  async getDetail(
    supabase: SupabaseClient,
    businessId: string,
  ): Promise<{ data: BusinessDetail | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.functions.invoke('ops-pending', {
        body: { businessId },
      });
      if (error) return { data: null, error: new Error(error.message ?? 'Forbidden') };
      if (!data?.business) return { data: null, error: new Error('Not found') };
      return { data: data.business as BusinessDetail, error: null };
    } catch (e) {
      return { data: null, error: e instanceof Error ? e : new Error(String(e)) };
    }
  },

  /**
   * Approve a business.
   *
   * ops-approve refuses with 409 `no_host_agreement` when the business has
   * never accepted the Host Agreement, because approving publishes it AND
   * makes it commission-chargeable under terms it never consented to. That
   * refusal is surfaced as `needsAgreementOverride` so the caller can offer
   * the deliberate override rather than showing a dead "Approval failed".
   *
   * functions.invoke collapses every non-2xx into the same generic message,
   * so the body has to be read off error.context (the raw Response) to get
   * the reason out. Without that the founder sees no explanation at all.
   */
  async approve(
    supabase: SupabaseClient,
    businessId: string,
    ownerId: string,
    // `reason` is now a REQUIRED branded Reason (fix round 1, item 5) — the
    // server refuses a portal-origin call with no reason anyway
    // (`parseApproveBody`'s `fromPortal` check, commit 3c8b4b2); the type
    // makes "forgot to pass one" a compile error instead of a 400 the caller
    // has to notice at runtime. `kycReviewed` (same commit): only documents
    // the admin actually opened are approved/purged — REQUIRED too (fix
    // round 1, item 4: "always send the array, [] when the owner truly has
    // none"), every entry keyed by `id`. A doc with no `id` throws rather
    // than being silently dropped: `id` comes from ops-pending's detail
    // response (Task 23 addition), so a missing one means that plumbing
    // broke, not that the doc doesn't matter.
    reason: Reason,
    kycReviewed: Array<{ id: string | undefined; filePath: string | null }>,
    opts?: { overrideNoAgreement?: boolean },
  ): Promise<{
    error: Error | null;
    needsAgreementOverride?: boolean;
    /** Phase 25: why the agreement gate refused — none on record vs an older signed version. */
    agreementIssue?: 'missing' | 'outdated';
    signedVersion?: string | null;
    requiredVersion?: string | null;
  }> {
    // Computed OUTSIDE the try block below on purpose: this is a programmer
    // error (the plumbing that stamps `id` onto every KYC doc broke), not a
    // recoverable server outcome — it must throw/reject, not collapse into
    // the same `{ error }` shape a 409 or a network blip returns.
    const reviewed = kycReviewed.map((d) => {
      if (!d.id) throw new Error('A KYC document is missing its id — cannot record what was reviewed.');
      return { id: d.id, filePath: d.filePath };
    });
    try {
      const { data, error } = await supabase.functions.invoke('ops-approve', {
        body: {
          businessId,
          ownerId,
          reason,
          kycReviewed: reviewed,
          ...(opts?.overrideNoAgreement ? { overrideNoAgreement: true } : {}),
        },
      });
      if (error) {
        const ctx = (error as { context?: { json?: () => Promise<Record<string, string | null | undefined>> } })
          ?.context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            if (body?.error === 'no_host_agreement') {
              return {
                error: new Error(body.message ?? 'No Host Agreement on record.'),
                needsAgreementOverride: true,
                agreementIssue: 'missing',
              };
            }
            if (body?.error === 'host_agreement_outdated') {
              return {
                error: new Error(body.message ?? 'Host Agreement out of date.'),
                needsAgreementOverride: true,
                agreementIssue: 'outdated',
                signedVersion: body.signedVersion ?? null,
                requiredVersion: body.requiredVersion ?? null,
              };
            }
            if (body?.message || body?.error) {
              return { error: new Error(body.message || body.error || 'Approval failed') };
            }
          } catch {
            // Not JSON — fall through to the generic message below.
          }
        }
        return { error: new Error(error.message ?? 'Approval failed') };
      }
      if (!data?.approved) return { error: new Error('Approval failed') };
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e : new Error(String(e)) };
    }
  },

  async review(
    supabase: SupabaseClient,
    payload: Omit<ReviewPayload, 'reason'> & { reason: Reason },
  ): Promise<{ error: Error | null }> {
    try {
      const { data, error } = await supabase.functions.invoke('ops-review', { body: payload });
      if (error) return { error: new Error(error.message ?? 'Review action failed') };
      if (!data?.ok) return { error: new Error('Review action failed') };
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e : new Error(String(e)) };
    }
  },
};
